import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "../../store/actions";
import axios from "../../utils/axios";
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    InputGroup,
    InputGroupText,
    Badge,
    Input,
    Button,
    Table,
    Spinner
} from 'reactstrap';
import { FiEdit, FiTrash2, FiPlus, FiSearch } from 'react-icons/fi';

const JobCategories = (props) => {
    document.title = "Job Categories | Equity Circle - Admin Dashboard";
    const breadcrumbItems = [
        { title: "Job Management", link: "#" },
        { title: "All Job Categories", link: "#" },
    ];

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    useEffect(() => {
        props.setBreadcrumbItems('Job Categories', breadcrumbItems);
        fetchCategories();
    }, []);

    const fetchCategories = () => {
        setLoading(true);
        axios.get('/api/job-categories')
            .then(response => {
                setCategories(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching categories:', error);
                toast.error('Failed to fetch job categories');
                setLoading(false);
            });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const apiCall = currentCategory
            ? axios.put(`/api/job-categories/${currentCategory.id}`, formData)
            : axios.post('/api/job-categories', formData);

        apiCall
            .then(response => {
                toast.success(`Category ${currentCategory ? 'updated' : 'created'} successfully`);
                setShowModal(false);
                fetchCategories();
                resetForm();
            })
            .catch(error => {
                console.error('Error saving category:', error);
                toast.error(error.response?.data?.message || 'Failed to save category');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleEdit = (category) => {
        setCurrentCategory(category);
        setFormData({
            name: category.name,
            description: category.description || ''
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                axios.delete(`/api/job-categories/${id}`)
                    .then(() => {
                        toast.success('Category deleted successfully');
                        fetchCategories();
                    })
                    .catch(error => {
                        console.error('Error deleting category:', error);
                        toast.error('Failed to delete category');
                    });
            }
        });
    };

    const resetForm = () => {
        setFormData({ name: '', description: '' });
        setCurrentCategory(null);
    };

    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <React.Fragment>
            <ToastContainer />
            <Row>
                <Col lg="12">
                    <Card>
                        <CardBody>
                            <Row className="mb-4">
                                <Col md="6">
                                    <h4 className="mb-0">Job Categories</h4>
                                    <p className="text-muted mt-2">Manage and review all job categories</p>
                                </Col>
                                <Col md="6" className="text-end">
                                    <Button 
                                        color="primary" 
                                        onClick={() => {
                                            resetForm();
                                            setShowModal(true);
                                        }}
                                    >
                                        <FiPlus className="me-1" /> Add Category
                                    </Button>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col md={4}>
                                    <InputGroup>
                                        <InputGroupText><FiSearch /></InputGroupText>
                                        <Input 
                                            placeholder="Search categories..." 
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row>

                            {loading ? (
                                <div className="text-center my-5">
                                    <Spinner color="primary" />
                                    <p className="mt-2">Loading categories...</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCategories.length > 0 ? (
                                                filteredCategories.map((category, index) => (
                                                    <tr key={category.id}>
                                                        <th scope="row">{index + 1}</th>
                                                        <td>{category.name}</td>
                                                        <td>{category.description || '-'}</td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button 
                                                                    color="primary" 
                                                                    size="sm" 
                                                                    onClick={() => handleEdit(category)}
                                                                >
                                                                    <FiEdit size={14} />
                                                                </Button>
                                                                {/* <Button 
                                                                    color="danger" 
                                                                    size="sm"
                                                                    onClick={() => handleDelete(category.id)}
                                                                >
                                                                    <FiTrash2 size={14} />
                                                                </Button> */}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-4">
                                                        No categories found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Add/Edit Category Modal */}
            <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
                <ModalHeader toggle={() => setShowModal(false)}>
                    {currentCategory ? 'Edit Category' : 'Add New Category'}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <FormGroup className="mb-3">
                            <Label>Name <span className="text-danger">*</span></Label>
                            <Input 
                                type="text" 
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input 
                                type="textarea" 
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows="3"
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            type="button" 
                            color="light" 
                            onClick={() => {
                                setShowModal(false);
                                resetForm();
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    {currentCategory ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                currentCategory ? 'Update' : 'Create'
                            )}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default connect(null, { setBreadcrumbItems })(JobCategories);